import { getClub, type ClubId } from "@clubap/shared";
import { useEffect, useState } from "react";
import { depositToClub } from "../economy/clubRoom.js";

interface Props {
  clubId: ClubId;
  /** Lo que hay en la caja. Lo manda `App`, que es quien lo tiene fresco. */
  treasury: number;
  /** Tus monedas: de acá sale lo que aportes. */
  balance: number;
  onDeposited: (treasury: number, balance: number) => void;
  onClose: () => void;
}

/** Cuánto se puede aportar de un toque, para no escribir una cifra a mano cada vez. */
const ATAJOS = [10, 50, 100] as const;

/**
 * La caja común del club: aportar, y nada más.
 *
 * Esto es lo que un miembro CUALQUIERA puede hacer en la sala, y el motivo de que la
 * caja exista separada del saldo de nadie: el cuarto se paga entre todos y lo amuebla el
 * presidente. Por eso acá no hay botón de sacar — no está sin escribir, está decidido
 * que no lo haya: una caja de la que se puede retirar convierte al presidente en el
 * único con llave de un dinero ajeno, y ese es un poder que nadie le dio al nombrarlo.
 *
 * Lo que sí se ve es en qué se está yendo: el saldo de la caja está arriba, y lo que se
 * compra con ella se queda en la sala a la vista de todos.
 */
export function ClubTreasuryPanel({ clubId, treasury, balance, onDeposited, onClose }: Props) {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const club = getClub(clubId);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cifra = Number.parseInt(amount, 10);
  const valida = Number.isInteger(cifra) && cifra > 0 && cifra <= balance;

  const aportar = async (cuanto: number) => {
    setBusy(true);
    setError(null);

    try {
      const result = await depositToClub(clubId, cuanto);
      onDeposited(result.treasury, result.balance);
      setAmount("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo aportar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay">
      <div className="panel sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">Caja de {club?.name ?? "el club"}</h2>
          <span className="sheet__coins">◉ {treasury}</span>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {error && (
          <p className="sheet__error" role="alert">
            {error}
          </p>
        )}

        <section className="sheet__group">
          <h3 className="sheet__slot">Aportar</h3>

          <div className="caja-atajos">
            {ATAJOS.map((cuanto) => (
              <button
                className="btn btn--small"
                type="button"
                key={cuanto}
                disabled={busy || balance < cuanto}
                title={balance < cuanto ? `Te faltan ${cuanto - balance}` : `Aportar ${cuanto}`}
                onClick={() => void aportar(cuanto)}
              >
                ◉ {cuanto}
              </button>
            ))}
          </div>

          <div className="caja-libre">
            <input
              className="join__input"
              type="number"
              min={1}
              max={balance}
              value={amount}
              placeholder="Otra cantidad"
              disabled={busy}
              onChange={(event) => setAmount(event.target.value)}
            />
            <button
              className="btn btn--small"
              type="button"
              disabled={busy || !valida}
              onClick={() => void aportar(cifra)}
            >
              Aportar
            </button>
          </div>

          <p className="sheet__hint">Tienes ◉ {balance}.</p>
        </section>

        <p className="sheet__hint">
          Lo que entra a la caja es del club y no se devuelve. Con ella compra muebles el
          presidente, y lo que ponga se queda en esta sala.
        </p>
      </div>
    </div>
  );
}
